import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { handleOnSettled } from '@utils/formHelpers';
import { useCreateExternalSchedule } from '@services/schedules/external/create';
import { useUpdateExternalSchedule } from '@services/schedules/external/update';
import {
  ConcreteDirectOrder,
  ExternalScheduleRespSuccess,
  ScheduleMaterial,
  ScheduleNote,
} from '@services/schedules/external/types';
import { getHours, getMinutes, isValid, set } from 'date-fns';
import { toTenantWallClock } from '@utils/timeZone';
import { useErrors } from '@hooks/errors';
import { ExternalScheduleFormProps, ExternalScheduleFormValues } from './types';
import { defaultValues, formSchema } from './constants';
import { FormProps } from '@forms/types';
import { hasDirtyValues } from '@utils/formHelpers';

export const useExternalScheduleForm = ({
  successHandler,
  concreteDirect,
  jobId,
  taskId,
  scheduleId,
  schedule,
  note,
}: FormProps<ExternalScheduleFormProps, ExternalScheduleRespSuccess>) => {
  const handleErrors = useErrors();

  const form = useForm<ExternalScheduleFormValues>({
    resolver: zodResolver(formSchema(concreteDirect, schedule)),
    defaultValues,
  });

  const callback = (resp: ExternalScheduleRespSuccess) => {
    if (!resp) {
      handleErrors('request_failed');
      return;
    }
    handleOnSettled<ExternalScheduleRespSuccess>(
      resp,
      successHandler,
      () =>
        handleErrors(resp.errors, (key, message) =>
          form.setError(key as keyof ExternalScheduleFormValues, { message }),
        ),
      true,
    );
  };

  const create = useCreateExternalSchedule({ callback });
  const reschedule = useUpdateExternalSchedule({ callback });

  const onSubmit = async ({
    date,
    time,
    confirmed,
    note: content,
    extraComment,
    externalContactId,
    externalSiteId,
    additionalInformation,
    id,
    isExactQuantity,
    itemExternalId,
    additive,
    pourElement,
    pourMethod,
    rateType,
    quantityRate,
    quantity,
    slump,
    materialOptions,
  }: ExternalScheduleFormValues) => {
    if (!jobId || !taskId) {
      handleErrors('form_submit_failed');
      return;
    }
    if (!date || !isValid(date)) {
      form.setError('date', { message: 'date.required' });
      return;
    }
    if (!time || !isValid(time)) {
      form.setError('time', { message: 'time.required' });
      return;
    }
    const startsAt = new Date(
      +toTenantWallClock(set(date, { hours: getHours(time), minutes: getMinutes(time) })),
    );
    const cdApiOrder: ConcreteDirectOrder | undefined = concreteDirect
      ? {
          jobId,
          externalSiteId,
          additionalInformation,
          orderItems: [
            {
              id: id?.[0],
              isExactQuantity,
              itemExternalId,
              localStartDatetime: startsAt.toISOString(),
              orderItemAdditions: additive?.itemExternalId
                ? [{ ...additive, id: additive.id[0] }]
                : [],
              pourElement,
              pourMethod,
              qtyPerHour: rateType === 'QTY_PER_HOUR' ? quantityRate : undefined,
              quantity,
              slump,
              spacingInMinutes: rateType === 'SPACING_IN_MINUTES' ? quantityRate : undefined,
            },
          ],
        }
      : undefined;

    const noteObject = content
      ? ({
          id: note?.id,
          content,
          referableId: taskId,
          referableType: 'Task',
        } as ScheduleNote)
      : undefined;

    const material: ScheduleMaterial | undefined = hasDirtyValues(
      form.formState.dirtyFields.materialOptions,
    )
      ? {
          optionsAttributes: materialOptions.map(
            ({ id, includeEstimated, estimatedQuantity, finalQuantity }) => ({
              id,
              estimatedQuantity: includeEstimated ? estimatedQuantity : finalQuantity,
              finalQuantity,
            }),
          ),
        }
      : undefined;

    const data = {
      jobId,
      scheduleId,
      schedule: {
        taskId,
        startsAt,
        confirmed,
        externalContactId,
        extraComment,
        cdApiOrder,
        material,
      },
      note: noteObject,
    };

    if (scheduleId && scheduleId !== 'new') reschedule.mutate(data);
    else create.mutate(data);
  };

  return {
    form,
    onSubmit,
    locked: create.isLoading || reschedule.isLoading,
  };
};
